import { useState, useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Calendar, Clock, MapPin, Phone, Mail, ChevronRight,
  Award, ExternalLink, CheckCircle, BookOpen, Users,
  ArrowLeft, Info, Banknote, Scale, GraduationCap, Target,
} from "lucide-react";
import { CORSI } from "@/data/corsiData";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const MESI_IT = {
  gennaio: 0, febbraio: 1, marzo: 2, aprile: 3, maggio: 4, giugno: 5,
  luglio: 6, agosto: 7, settembre: 8, ottobre: 9, novembre: 10, dicembre: 11,
};

// Converte una data in formato "13 Aprile 2026" in timestamp, per poterle ordinare cronologicamente
function parseDataItaliana(str) {
  if (!str) return null;
  const m = str.trim().toLowerCase().match(/(\d{1,2})\s+([a-zàéìòù]+)\s+(\d{4})/i);
  if (!m) return null;
  const meseIdx = MESI_IT[m[2]];
  if (meseIdx === undefined) return null;
  return new Date(Number(m[3]), meseIdx, Number(m[1])).getTime();
}

function SectionTitle({ icon: Icon, children }) {
  return (
    <h2 className="text-xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-5 flex items-center gap-2">
      {Icon && <Icon size={20} className="text-[var(--confcom-blue)]" aria-hidden="true" />}
      {children}
    </h2>
  );
}

export default function CorsoDettaglioPage() {
  const { slug } = useParams();
  const corso = CORSI.find((c) => c.slug === slug);
  const altriCorsi = CORSI.filter((c) => c.slug !== slug).slice(0, 3);

  // Sessioni con date alternative di inizio/fine/esame (se disponibili in più di una)
  const sessioni = corso?.sessioni && corso.sessioni.length > 1 ? corso.sessioni : null;

  // La sessione che parte prima è selezionata di default
  const defaultSessioneIdx = useMemo(() => {
    if (!sessioni) return 0;
    let bestIdx = 0;
    let bestTime = parseDataItaliana(sessioni[0].dataInizio) ?? Infinity;
    sessioni.forEach((s, i) => {
      const t = parseDataItaliana(s.dataInizio);
      if (t !== null && t < bestTime) { bestTime = t; bestIdx = i; }
    });
    return bestIdx;
  }, [sessioni]);

  const [sessioneIdx, setSessioneIdx] = useState(defaultSessioneIdx);

  // Riallinea la selezione se si cambia corso o cambiano le sessioni disponibili
  useEffect(() => {
    setSessioneIdx(defaultSessioneIdx);
  }, [corso?.slug, defaultSessioneIdx]);

  if (!corso) {
    return (
      <main id="main-content" className="py-20 text-center">
        <p className="text-[var(--confcom-text-secondary)]">Corso non trovato.</p>
        <Link to="/formazione/corsi-in-partenza" className="text-[var(--confcom-blue)] underline mt-4 inline-block">
          Torna al Calendario Corsi
        </Link>
      </main>
    );
  }

  const sessioneAttiva = sessioni ? sessioni[sessioneIdx] : { dataInizio: corso.dataInizio, dataFine: corso.dataFine, dataEsame: corso.dataEsame };


  return (
    <main id="main-content" data-testid={`corso-dettaglio-${corso.slug}`}>

      {/* ── Hero ── */}
      <section className="relative h-[300px] md:h-[360px] overflow-hidden">
        <img
          src={corso.immagine}
          alt={corso.titolo}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(4,30,66,0.82)" }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative h-full flex flex-col justify-end pb-12">

          {/* Back link */}
          <Link
            to="/formazione/corsi-in-partenza"
            className="flex w-fit items-center gap-2 text-sm text-white/70 hover:text-white mb-4 transition-colors"
            data-testid="back-to-corsi"
          >
            <ArrowLeft size={16} /> Calendario Corsi
          </Link>

          {/* Categoria badge */}
          <div
            style={{
              display: "inline-flex", alignItems: "center",
              background: "rgba(255,255,255,0.15)",
              color: "#fff",
              fontSize: "0.72rem", fontWeight: 700,
              textTransform: "uppercase", letterSpacing: "0.14em",
              padding: "0.3rem 0.9rem", borderRadius: "2rem",
              marginBottom: "0.85rem", width: "fit-content",
            }}
          >
            {corso.categoria}
          </div>

          <h1 className="text-3xl md:text-4xl font-bold text-white font-['Encode_Sans_Semi_Condensed'] tracking-tight max-w-3xl">
            {corso.titolo}
          </h1>
          {corso.sottotitolo && (
            <p className="mt-2 text-white/65 text-base italic">{corso.sottotitolo}</p>
          )}
        </div>
      </section>

      {/* ── Corpo ── */}
      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">

            {/* ── Colonna sinistra 2/3 ── */}
            <div className="lg:col-span-2" style={{ display: "flex", flexDirection: "column", gap: "3rem" }}>

              {/* Finalità */}
              <div>
                <SectionTitle icon={Info}>Finalità e contenuti</SectionTitle>
                {corso.finalita.split("\n\n").map((par, i) => (
                  <p key={i} className="text-[var(--confcom-text-secondary)] leading-relaxed mb-4">
                    {par}
                  </p>
                ))}
              </div>

              {/* Destinatari */}
              {corso.destinatari && (
                <div>
                  <SectionTitle icon={Users}>A chi è rivolto</SectionTitle>
                  <div style={{ display: "flex", flexDirection: "column", gap: "0.65rem" }}>
                    {corso.destinatari.map((d, i) => (
                      <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: "0.65rem" }}>
                        <div className="w-2 h-2 rounded-full shrink-0 mt-2 bg-[var(--confcom-blue)]" />
                        <span className="text-sm text-[var(--confcom-text-primary)] leading-relaxed">{d}</span>
                      </div>
                    ))}
                  </div>
                  {corso.accreditamenti && (
                    <div style={{ marginTop: "1.25rem", display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                      {corso.accreditamenti.map((a, i) => (
                        <span
                            key={i}
                            style={{
                              background: "#EBF5FF", color: "var(--confcom-blue)",
                              border: "1px solid #BDD8F5",
                              fontSize: "0.78rem", fontWeight: 600,
                              padding: "0.3rem 0.75rem", borderRadius: "2rem",
                              display: "inline-flex", alignItems: "center", gap: "0.3rem",
                            }}
                          >
                            <Award size={12} /> {a}
                          </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Obiettivi */}
              {corso.obiettivi && (
                <div>
                  <SectionTitle icon={Target}>Obiettivi formativi</SectionTitle>
                  <div style={{ display: "flex", flexDirection: "column", gap: "0.55rem" }}>
                    {corso.obiettivi.map((o, i) => (
                      <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: "0.65rem" }}>
                        <CheckCircle size={15} className="text-[var(--confcom-blue)] shrink-0" style={{ marginTop: 2 }} />
                        <span className="text-sm text-[var(--confcom-text-secondary)] leading-relaxed">{o}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Programma */}
              <div>
                <SectionTitle icon={BookOpen}>Programma</SectionTitle>
                {corso.docente && (
                  <p className="text-sm text-[var(--confcom-text-secondary)] mb-5">
                    <strong>Formatore:</strong> {corso.docente}
                  </p>
                )}
                {corso.programma.map((blocco, i) => (
                  <div key={i} className="mb-5">
                    <h3 className="text-sm font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-2">
                      {blocco.titolo}
                    </h3>
                    <div style={{ display: "flex", flexDirection: "column", gap: "0.35rem", paddingLeft: "0.5rem" }}>
                      {blocco.voci.map((v, j) => (
                        <div key={j} style={{ display: "flex", alignItems: "flex-start", gap: "0.55rem" }}>
                          <div className="w-1.5 h-1.5 rounded-full shrink-0 mt-2 bg-[var(--confcom-blue)]" />
                          <span className="text-sm text-[var(--confcom-text-secondary)] leading-relaxed">{v}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                {corso.materialeDidattico && (
                  <div className="grid sm:grid-cols-2 gap-3 mt-2">
                    {corso.materialeDidattico.map((m, i) => (
                      <div
                        key={i}
                        className="flex items-start gap-3 p-4 bg-[#EEF4FB] rounded-xl border border-[#C8DDEF]"
                      >
                        <div className="w-2 h-2 rounded-full shrink-0 mt-1.5 bg-[var(--confcom-blue)]" />
                        <span className="text-sm text-[var(--confcom-text-primary)] leading-relaxed">{m}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Frequenza e sede */}
              {corso.frequenzaNota && (
                <div>
                  <SectionTitle icon={MapPin}>Frequenza e sede</SectionTitle>
                  <p className="text-[var(--confcom-text-secondary)] text-sm leading-relaxed">
                    {corso.frequenzaNota}
                  </p>
                </div>
              )}

              {/* Quota */}
              {corso.quotaNota ? (
                <div>
                  <SectionTitle icon={Banknote}>Quota di iscrizione</SectionTitle>
                  {corso.quotaNota.split("\n\n").map((par, i) => (
                    <p key={i} className="text-sm text-[var(--confcom-text-secondary)] leading-relaxed mb-3">{par}</p>
                  ))}
                </div>
              ) : corso.quota ? (
                <div>
                  <SectionTitle icon={Banknote}>Quota di iscrizione</SectionTitle>
                  <p className="text-base font-semibold text-[var(--confcom-text-primary)] mb-4">{corso.quota}</p>
                  {corso.notaEBIT && (
                    <div className="flex items-start gap-3 p-4 bg-[#F0FDF4] rounded-xl border border-[#86EFAC]">
                      <CheckCircle size={15} color="#16A34A" className="shrink-0 mt-0.5" />
                      <p className="text-sm text-[#15803D] font-medium">{corso.notaEBIT}</p>
                    </div>
                  )}
                </div>
              ) : null}

              {/* Normativa */}
              {corso.normativa && (
                <div>
                  <SectionTitle icon={Scale}>Normativa di riferimento</SectionTitle>
                  <a
                    href={corso.normativa}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm font-medium text-[var(--confcom-blue)] hover:gap-2 transition-all"
                    style={{ textDecoration: "none" }}
                  >
                    Regione Lazio – Formazione regolamentata {corso.normativaCodice}
                    <ExternalLink size={13} />
                  </a>
                </div>
              )}
            </div>

            {/* ── Sidebar 1/3 ── */}
            <div className="lg:col-span-1">
              <div
                className="bg-white rounded-2xl border border-[var(--confcom-border)] p-6 sticky top-[104px] lg:top-[168px] shadow-sm"
                data-testid={`sidebar-corso-${corso.slug}`}
              >
                {/* Codice */}
                <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1.5rem" }}>
                  <div
                    style={{
                      width: 48, height: 48, borderRadius: "0.75rem",
                      background: corso.categoriaColor,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <GraduationCap size={22} color="#fff" />
                  </div>
                  <div>
                    <p style={{ fontSize: "0.68rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.14em", color: "var(--confcom-text-muted)" }}>
                      ID corso
                    </p>
                    <p className="font-['Encode_Sans_Semi_Condensed']" style={{ fontSize: "1.4rem", fontWeight: 800, color: "var(--confcom-text-primary)", lineHeight: 1 }}>
                      {corso.codice}
                    </p>
                  </div>
                </div>

                {/* Info chiave */}
                <div style={{ display: "flex", flexDirection: "column", gap: "1rem", marginBottom: "1.5rem" }}>
                  <div>
                    <p style={{ fontSize: "0.68rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.12em", color: "var(--confcom-text-muted)", marginBottom: sessioni ? "0.4rem" : "0.15rem" }}>
                      {sessioni ? "Sessione di partenza" : "Data inizio"}
                    </p>
                    {sessioni ? (
                      <Select
                        value={String(sessioneIdx)}
                        onValueChange={(v) => setSessioneIdx(Number(v))}
                      >
                        <SelectTrigger
                          className="h-9 text-sm font-semibold text-[var(--confcom-text-primary)] border-[var(--confcom-border)] focus:ring-[var(--confcom-blue)] data-[state=open]:border-[var(--confcom-blue)]"
                          data-testid={`select-sessione-${corso.slug}`}
                          aria-label="Seleziona un'altra data di inizio corso"
                        >
                          <div className="flex items-center gap-2">
                            <Calendar size={14} className="text-[var(--confcom-blue)] shrink-0" aria-hidden="true" />
                            <SelectValue />
                          </div>
                        </SelectTrigger>
                        <SelectContent>
                          {sessioni.map((s, i) => (
                            <SelectItem
                              key={i}
                              value={String(i)}
                              className="focus:bg-[#EBF5FF] focus:text-[var(--confcom-blue)] data-[state=checked]:text-[var(--confcom-blue)] data-[state=checked]:font-semibold [&_svg]:text-[var(--confcom-blue)]"
                              data-testid={`select-sessione-${corso.slug}-opt-${i}`}
                            >
                              {s.dataInizio}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                        <Calendar size={13} className="text-[var(--confcom-blue)]" aria-hidden="true" />
                        <span style={{ fontSize: "0.88rem", fontWeight: 600, color: "var(--confcom-text-primary)" }}>{sessioneAttiva.dataInizio}</span>
                      </div>
                    )}
                  </div>
                  {sessioneAttiva.dataFine && <InfoRow icon={Calendar} label="Data fine" value={sessioneAttiva.dataFine} />}
                  {sessioneAttiva.dataEsame && <InfoRow icon={Calendar} label="Esame / Prova finale" value={sessioneAttiva.dataEsame} />}
                  <InfoRow icon={Clock} label="Durata" value={corso.durata} />
                  {corso.sede && <InfoRow icon={MapPin} label="Sede" value={corso.sede} />}
                </div>

                {/* Quota */}
                <div
                  style={{
                    background: "#EBF5FF", borderRadius: "0.65rem",
                    padding: "0.75rem 1rem", marginBottom: "1.25rem",
                  }}
                >
                  <p style={{ fontSize: "0.68rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.12em", color: "var(--confcom-text-muted)", marginBottom: "0.2rem" }}>
                    Quota
                  </p>
                  <p className="font-['Encode_Sans_Semi_Condensed']" style={{ fontSize: "0.95rem", fontWeight: 700, color: "var(--confcom-blue)" }}>
                    {corso.quota}
                  </p>
                </div>

                {/* CTA */}
                {corso.formIscrizione ? (
                  <Link
                    to={`/formazione/iscrizione-corso?corso=${corso.slug}&data=${encodeURIComponent(sessioneAttiva.dataInizio)}`}
                    data-testid={`cta-iscrizione-${corso.slug}`}
                    className="flex items-center justify-center gap-2 bg-[var(--confcom-blue)] text-white font-bold text-sm py-3 rounded-lg w-full hover:opacity-90 transition-opacity"
                    style={{ textDecoration: "none" }}
                  >
                    Iscriviti al corso <ChevronRight size={16} />
                  </Link>
                ) : (
                  <a
                    href={`mailto:${corso.contatti.email}`}
                    data-testid={`cta-iscrizione-${corso.slug}`}
                    className="flex items-center justify-center gap-2 bg-[var(--confcom-blue)] text-white font-bold text-sm py-3 rounded-lg w-full hover:opacity-90 transition-opacity"
                    style={{ textDecoration: "none" }}
                  >
                    Richiedi iscrizione <Mail size={14} />
                  </a>
                )}

                <hr className="my-5 border-[var(--confcom-border)]" />

                {/* Contatti */}
                <h3 className="text-sm font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-4">
                  Informazioni
                </h3>
                <div style={{ display: "flex", flexDirection: "column", gap: "0.65rem" }}>
                  <a
                    href={`tel:${corso.contatti.tel.split("–")[0].trim().replace(/[\s.]/g, "")}`}
                    className="flex items-center gap-3 text-sm text-[var(--confcom-text-secondary)] hover:text-[var(--confcom-blue)] transition-colors"
                    style={{ textDecoration: "none" }}
                  >
                    <Phone size={16} className="text-[var(--confcom-blue)] shrink-0" />
                    {corso.contatti.tel}
                  </a>
                  <a
                    href={`mailto:${corso.contatti.email}`}
                    className="flex items-center gap-3 text-sm text-[var(--confcom-text-secondary)] hover:text-[var(--confcom-blue)] transition-colors"
                    style={{ textDecoration: "none" }}
                  >
                    <Mail size={16} className="text-[var(--confcom-blue)] shrink-0" />
                    {corso.contatti.email}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Altri corsi ── */}
      {altriCorsi.length > 0 && (
        <section className="py-14 bg-[#F5F8FC]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-8">
              Potresti essere interessato anche a…
            </h2>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "1.25rem" }}>
              {altriCorsi.map((c) => (
                <Link
                  key={c.slug}
                  to={`/formazione/corsi-in-partenza/${c.slug}`}
                  data-testid={`related-corso-${c.slug}`}
                  style={{ textDecoration: "none" }}
                >
                  <div
                    className="hover:shadow-md transition-shadow"
                    style={{
                      background: "#fff", border: "1px solid var(--confcom-border)",
                      borderRadius: "0.75rem", overflow: "hidden",
                    }}
                  >
                    <img
                      src={c.immagine}
                      alt={c.titolo}
                      style={{ width: "100%", height: 140, objectFit: "cover", display: "block" }}
                    />
                    <div style={{ padding: "1rem" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.5rem" }}>
                        <span
                          style={{
                            background: c.categoriaColor, color: "#fff",
                            fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase",
                            letterSpacing: "0.08em", padding: "0.2rem 0.55rem", borderRadius: "2rem",
                          }}
                        >
                          {c.codice}
                        </span>
                        <span style={{ fontSize: "0.75rem", color: "var(--confcom-text-muted)" }}>{c.dataInizio}</span>
                      </div>
                      <p className="font-['Encode_Sans_Semi_Condensed']" style={{ fontSize: "0.9rem", fontWeight: 700, color: "var(--confcom-text-primary)", lineHeight: 1.3, marginBottom: "0.5rem" }}>
                        {c.titolo}
                      </p>
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-[var(--confcom-blue)]">
                        Scopri <ChevronRight size={12} />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div>
      <p style={{ fontSize: "0.68rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.12em", color: "var(--confcom-text-muted)", marginBottom: "0.15rem" }}>
        {label}
      </p>
      <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
        <Icon size={13} className="text-[var(--confcom-blue)]" />
        <span style={{ fontSize: "0.88rem", fontWeight: 600, color: "var(--confcom-text-primary)" }}>{value}</span>
      </div>
    </div>
  );
}
